import { produce } from 'immer';

import {
    CANCEL_CHANGES_REGIONS_EDITOR,
    CancelChangesRegionsEditor,
    CHANGE_FILTER_TEXT_REGIONS_EDITOR,
    CHANGE_GEO_TYPE_IN_PRICE_PACKAGE,
    CHANGE_TARGETING_IN_REGION_EDITOR,
    ChangeFilterTextRegionsEditor,
    CLEAR_REGIONS_EDITOR,
    CLEAR_SELECTED_ITEMS_REGIONS_EDITOR,
    ClearRegionsEditor,
    ClearSelectedRegionsEditorItems,
    CLOSE_REGIONS_EDITOR,
    CloseRegionsEditor,
    COLLAPSE_REGIONS_EDITOR_ITEM,
    CollapseRegionsEditorItem,
    DESELECT_QUICK_REGIONS_EDITOR_ITEM,
    DESELECT_REGIONS_EDITOR_ITEM,
    DeselectQuickRegionsEditorItem,
    DeselectRegionsEditorItem,
    EXPAND_REGIONS_EDITOR_ITEM,
    ExpandRegionsEditorItem,
    OPEN_REGIONS_EDITOR,
    OpenRegionsEditor,
    RegionsEditorAction,
    RESET_REGIONS_EDITOR,
    SELECT_QUICK_REGIONS_EDITOR_ITEM,
    SELECT_REGIONS_EDITOR_ITEM,
    SelectQuickRegionsEditorItem,
    SelectRegionsEditorItem,
    START_CHANGE_FILTER_TEXT_REGIONS_EDITOR,
    StartChangeFilterTextRegionsEditor
} from '../actions/regionsEditor';
import { TreeHelpers } from '../service/TreeHelpers/TreeHelpers';
import { Region } from '../typings/common';
import { GroupId } from '../typings/groups';
import { RegionsEditorState, RegionsQuickSelectMode } from '../typings/regionsEditor';
import { State } from '../typings/state';

type RegionsEditorsState = Record<GroupId, RegionsEditorState>;

export const defaultRegionsEditorData = {
    selectedMap: {},
    expandedMap: {},
    isOpen: false,
    searchValue: ''
};

const openRegionsEditor = (state: RegionsEditorsState, action: OpenRegionsEditor): RegionsEditorsState => {
    const { tree, selectedMap, geoType, isFixed, id } = action.data;
    const mapIdsAllRegions = TreeHelpers.updateMapForCurrentIds(TreeHelpers.getChildNodesIds(tree), {}, true);

    const listFirstSelectedIds = tree.reduce((acc: string[], node) => {
        const foundedNodes = TreeHelpers.getFirstEqualsTreeNodesList(node, selectedMap, true);
        acc = acc.concat(foundedNodes.map(foundedNode => foundedNode.id));

        return acc;
    }, []);

    const listExpandedIds = TreeHelpers.getIdsPathToCurrentNodes(tree, listFirstSelectedIds);

    const expandedMap = TreeHelpers.updateMapForCurrentIds(listExpandedIds, {}, true);

    return {
        ...state,
        [id]: {
            ...defaultRegionsEditorData,
            filteredMap: mapIdsAllRegions,
            expandedMap: expandedMap,
            isOpen: true,
            geoType: geoType,
            isFixed: isFixed,
            selectedMap: selectedMap
        }
    };
};

const selectRegionsEditorItem = (state: RegionsEditorsState, action: SelectRegionsEditorItem): RegionsEditorsState => {
    const currentRegionsEditor = state[action.id];
    const { selectedMap, expandedMap } = currentRegionsEditor;

    const listIdsForCurrentNode = TreeHelpers.getChildNodesIds([action.node]);
    const newSelectedMap = TreeHelpers.updateMapForCurrentIds(listIdsForCurrentNode, selectedMap, true);
    const newExpandedMap = TreeHelpers.updateMapForCurrentIds([action.node.id], expandedMap, true);

    return {
        ...state,
        [action.id]: {
            ...currentRegionsEditor,
            selectedMap: newSelectedMap,
            expandedMap: newExpandedMap
        }
    };
};

const deselectRegionsEditorItem = (
    state: RegionsEditorsState,
    action: DeselectRegionsEditorItem
): RegionsEditorsState => {
    const currentRegionsEditor = state[action.id];
    const { selectedMap } = currentRegionsEditor;

    const listIdsForCurrentNode = TreeHelpers.getChildNodesIds([action.node]);
    const newSelectedMap = TreeHelpers.updateMapForCurrentIds(listIdsForCurrentNode, selectedMap, false);

    return {
        ...state,
        [action.id]: {
            ...currentRegionsEditor,
            selectedMap: newSelectedMap
        }
    };
};

const expandRegionsEditorItem = (state: RegionsEditorsState, action: ExpandRegionsEditorItem): RegionsEditorsState => {
    const currentRegionsEditor = state[action.id];
    const { expandedMap } = currentRegionsEditor;

    const newExpandedMap = TreeHelpers.updateMapForCurrentIds([action.node.id], expandedMap, true);

    return {
        ...state,
        [action.id]: {
            ...currentRegionsEditor,
            expandedMap: newExpandedMap
        }
    };
};

const collapseRegionsEditorItem = (
    state: RegionsEditorsState,
    action: CollapseRegionsEditorItem
): RegionsEditorsState => {
    const currentRegionsEditor = state[action.id];
    const { expandedMap } = currentRegionsEditor;

    const newExpandedMap = TreeHelpers.updateMapForCurrentIds([action.node.id], expandedMap, false);

    return {
        ...state,
        [action.id]: {
            ...currentRegionsEditor,
            expandedMap: newExpandedMap
        }
    };
};

const selectQuickRegionsEditorItem = (
    state: RegionsEditorsState,
    action: SelectQuickRegionsEditorItem
): RegionsEditorsState => {
    const { selectedMap: quickSelectedMap, tree, quickSelectMode } = action;
    const currentRegionsEditor = state[action.id];
    const { expandedMap, selectedMap } = currentRegionsEditor;

    const firstSelectedNodes = TreeHelpers.getFirstEqualsTreeNodesList<Region>(
        {
            id: '0',
            name: 'root',
            inner: tree
        },
        quickSelectedMap,
        true
    );
    const listFirstSelectedIds = firstSelectedNodes.map(node => node.id);
    const expandedListIds = TreeHelpers.getIdsPathToCurrentNodes(tree, listFirstSelectedIds);
    const listIdsForCurrentNodes = Object.keys(quickSelectedMap);

    const newSelectedMap = TreeHelpers.updateMapForCurrentIds(
        listIdsForCurrentNodes,
        quickSelectMode === RegionsQuickSelectMode.CHANGE ? selectedMap : {},
        true
    );
    const newExpandedMap = TreeHelpers.updateMapForCurrentIds(
        expandedListIds,
        quickSelectMode === RegionsQuickSelectMode.CHANGE ? expandedMap : {},
        true
    );

    return {
        ...state,
        [action.id]: {
            ...currentRegionsEditor,
            selectedMap: newSelectedMap,
            expandedMap: newExpandedMap,
            idForScrolling: listFirstSelectedIds[0]
        }
    };
};

const deselectQuickRegionsEditorItem = (
    state: RegionsEditorsState,
    action: DeselectQuickRegionsEditorItem
): RegionsEditorsState => {
    const currentRegionsEditor = state[action.id];
    const { selectedMap } = currentRegionsEditor;
    const { selectedMap: quickSelectedMap, tree, quickSelectMode } = action;

    const firstSelectedNodes = TreeHelpers.getFirstEqualsTreeNodesList<Region>(
        {
            id: '0',
            name: 'root',
            inner: tree
        },
        quickSelectedMap,
        true
    );
    const listFirstSelectedIds = firstSelectedNodes.map(node => node.id);
    const listIdsForCurrentNodes = Object.keys(quickSelectedMap);

    const newSelectedMap = TreeHelpers.updateMapForCurrentIds(
        listIdsForCurrentNodes,
        quickSelectMode === RegionsQuickSelectMode.CHANGE ? selectedMap : {},
        false
    );

    return {
        ...state,
        [action.id]: {
            ...currentRegionsEditor,
            selectedMap: newSelectedMap,
            idForScrolling: listFirstSelectedIds[0]
        }
    };
};

const changeFilterTextRegionsEditor = (
    state: RegionsEditorsState,
    action: ChangeFilterTextRegionsEditor
): RegionsEditorsState => {
    const { value } = action;
    const trimmedValue = value.trim();
    const currentRegionsEditor = state[action.id];
    const mapIdsAllRegions = TreeHelpers.updateMapForCurrentIds(TreeHelpers.getChildNodesIds(action.tree), {}, true);

    if (trimmedValue.length === 0) {
        return {
            ...state,
            [action.id]: {
                ...currentRegionsEditor,
                filteredMap: mapIdsAllRegions
            }
        };
    }

    const { filteredMap, expandedMap, listFoundedIds } = TreeHelpers.getUpdatedBySearchTreeParams(
        action.tree,
        trimmedValue
    );

    return {
        ...state,
        [action.id]: {
            ...currentRegionsEditor,
            expandedMap,
            filteredMap,
            idForScrolling: listFoundedIds[0]
        }
    };
};

const startChangeFilterTextRegionsEditor = (
    state: RegionsEditorsState,
    action: StartChangeFilterTextRegionsEditor
): RegionsEditorsState => {
    const currentRegionsEditor = state[action.id];

    return {
        ...state,
        [action.id]: {
            ...currentRegionsEditor,
            searchValue: action.value
        }
    };
};

const clearSelectedRegionsEditorItems = (
    state: RegionsEditorsState,
    action: ClearSelectedRegionsEditorItems
): RegionsEditorsState => {
    const currentRegionsEditor = state[action.id];

    return {
        ...state,
        [action.id]: {
            ...currentRegionsEditor,
            selectedMap: {}
        }
    };
};

const cancelChangesRegionsEditor = (
    state: RegionsEditorsState,
    action: CancelChangesRegionsEditor
): RegionsEditorsState => {
    const currentRegionsEditor = state[action.id];

    return {
        ...state,
        [action.id]: {
            ...currentRegionsEditor,
            selectedMap: action.selectedMap,
            isOpen: false
        }
    };
};

const closeRegionsEditor = (state: RegionsEditorsState, action: CloseRegionsEditor): RegionsEditorsState => {
    const currentRegionsEditor = state[action.id];

    return {
        ...state,
        [action.id]: {
            ...currentRegionsEditor,
            isOpen: false
        }
    };
};

const clearRegionsEditor = (state: RegionsEditorsState, action: ClearRegionsEditor): RegionsEditorsState => {
    const currentRegionsEditor = state[action.id];
    const mapIdsAllRegions = TreeHelpers.updateMapForCurrentIds(TreeHelpers.getChildNodesIds(action.tree), {}, true);

    return {
        ...state,
        [action.id]: {
            ...currentRegionsEditor,
            ...defaultRegionsEditorData,
            filteredMap: mapIdsAllRegions
        }
    };
};

export function regionsEditor(
    state: RegionsEditorsState = {},
    action: RegionsEditorAction,
    fullState: State
): RegionsEditorsState {
    switch (action.type) {
        case OPEN_REGIONS_EDITOR:
            return openRegionsEditor(state, action);
        case SELECT_REGIONS_EDITOR_ITEM:
            return selectRegionsEditorItem(state, action);
        case DESELECT_REGIONS_EDITOR_ITEM:
            return deselectRegionsEditorItem(state, action);
        case EXPAND_REGIONS_EDITOR_ITEM:
            return expandRegionsEditorItem(state, action);
        case COLLAPSE_REGIONS_EDITOR_ITEM:
            return collapseRegionsEditorItem(state, action);
        case SELECT_QUICK_REGIONS_EDITOR_ITEM:
            return selectQuickRegionsEditorItem(state, action);
        case DESELECT_QUICK_REGIONS_EDITOR_ITEM:
            return deselectQuickRegionsEditorItem(state, action);
        case START_CHANGE_FILTER_TEXT_REGIONS_EDITOR:
            return startChangeFilterTextRegionsEditor(state, action);
        case CHANGE_FILTER_TEXT_REGIONS_EDITOR:
            return changeFilterTextRegionsEditor(state, action);
        case CLEAR_SELECTED_ITEMS_REGIONS_EDITOR:
            return clearSelectedRegionsEditorItems(state, action);
        case CANCEL_CHANGES_REGIONS_EDITOR:
            return cancelChangesRegionsEditor(state, action);
        case CLOSE_REGIONS_EDITOR:
            return closeRegionsEditor(state, action);
        case RESET_REGIONS_EDITOR:
            return {};
        case CLEAR_REGIONS_EDITOR:
            return clearRegionsEditor(state, action);
        case CHANGE_GEO_TYPE_IN_PRICE_PACKAGE:
            return produce(state, draft => {
                draft[action.id].geoType = action.geoType;
            });
        case CHANGE_TARGETING_IN_REGION_EDITOR:
            return produce(state, draft => {
                draft[action.id].isFixed = action.isFixed;
            });
        default:
            return state;
    }
}
